import { DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons'
import { Button, Input, Typography } from 'antd'
import { RolodexDocumentEntry, TaggedDocumentField } from 'features/files/types'
import * as React from 'react'
import styled from 'styled-components'

const { Text } = Typography

/**
 * Props for document tagging components
 */
export interface DocumentTaggingProps {
  document: RolodexDocumentEntry | null
  fields: ReadonlyArray<TaggedDocumentField>
  onFieldsChange: (fields: ReadonlyArray<TaggedDocumentField>) => void
  onSave: () => Promise<void>
  saving?: boolean
  defaultKeys?: ReadonlyArray<string>
  className?: string
}

const Container = styled.div`
  background: ${({ theme }) => theme.forecast.cardBackground};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 16px;
`

const TagFieldRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
`

const TagFieldInputs = styled.div`
  flex: 1;
  display: flex;
  gap: 8px;
`

const TagFieldActions = styled.div`
  display: flex;
  gap: 8px;
`

const SaveButton = styled(Button)`
  margin-top: 16px;
  font-weight: 600;
  height: 40px;
  border-radius: 6px;
`

const AddTagButton = styled(Button)`
  border-style: dashed;
  border-radius: 6px;
  margin-top: 8px;
`

interface TagFieldProps {
  tag: TaggedDocumentField
  index: number
  onUpdate: (index: number, field: 'key' | 'value', value: string) => void
  onRemove: (index: number) => void
}

const TagField: React.FC<TagFieldProps> = ({
  tag,
  index,
  onUpdate,
  onRemove,
}) => (
  <TagFieldRow>
    <TagFieldInputs>
      <Input
        placeholder='Key'
        value={tag.key}
        onChange={(e) => onUpdate(index, 'key', e.target.value)}
        style={{ flex: 1 }}
      />
      <Input
        placeholder='Value'
        value={tag.value}
        onChange={(e) => onUpdate(index, 'value', e.target.value)}
        style={{ flex: 1 }}
      />
    </TagFieldInputs>
    <TagFieldActions>
      <Button type='text' size='small' onClick={() => onRemove(index)} danger>
        <DeleteOutlined />
      </Button>
    </TagFieldActions>
  </TagFieldRow>
)

export const DocumentTagging: React.FC<DocumentTaggingProps> = ({
  document,
  fields,
  onFieldsChange,
  onSave,
  saving = false,
  className,
}) => {
  const addTagField = React.useCallback(() => {
    onFieldsChange([...fields, { key: '', value: '' }])
  }, [fields, onFieldsChange])

  const updateTagField = React.useCallback(
    (index: number, field: 'key' | 'value', value: string) => {
      const newTags = fields.map((tag, i) =>
        i === index ? { ...tag, [field]: value } : tag
      )
      onFieldsChange(newTags)
    },
    [fields, onFieldsChange]
  )

  const removeTagField = React.useCallback(
    (index: number) => {
      const newTags = fields.filter((_, i) => i !== index)
      onFieldsChange(newTags)
    },
    [fields, onFieldsChange]
  )

  if (!document) {
    return (
      <Container className={className}>
        <Text
          strong
          style={{ fontSize: 16, marginBottom: 12, display: 'block' }}>
          Document Fields
        </Text>
        <Text type='secondary'>Select a document to edit fields</Text>
      </Container>
    )
  }

  return (
    <Container className={className}>
      <Text strong style={{ fontSize: 16, marginBottom: 12, display: 'block' }}>
        Tagged Fields
      </Text>

      <Text type='secondary' style={{ marginBottom: 16, display: 'block' }}>
        Tag fields within this document. These will be stored alongside the
        document.
      </Text>

      {fields.map((tag, index) => (
        <TagField
          key={index}
          tag={tag}
          index={index}
          onUpdate={updateTagField}
          onRemove={removeTagField}
        />
      ))}

      <AddTagButton type='dashed' onClick={addTagField} block>
        <PlusOutlined /> Add Field
      </AddTagButton>

      <SaveButton type='text' onClick={onSave} loading={saving} block>
        <SaveOutlined /> Save Fields
      </SaveButton>
    </Container>
  )
}
